"use client";

import { c as _c } from "react/compiler-runtime";
import { ShoppingBag, Trash, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ProductImage } from "@/components/ui/product-image";
import { QuantityPicker } from "@/components/ui/quantity-picker";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { useFormatMoney } from "@/hooks/useFormatMoney";
import { trackRemoveFromCart, trackViewCart } from "@/lib/analytics/gtm";
import { totalDiscount } from "@/lib/utils/discounts";
import { extractBasePath } from "@/lib/utils/path";
export function CartDrawer() {
  const $ = _c(67);
  const {
    cart,
    loading,
    updating,
    isOpen,
    closeCart,
    updateItem,
    removeItem,
    itemCount
  } = useCart();
  const t = useTranslations("cart");
  const tc = useTranslations("common");
  const money = useFormatMoney();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = extractBasePath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const basePath = t0;
  const viewCartFiredRef = useRef(false);
  const prevPathnameRef = useRef(pathname);
  let t1;
  let t2;
  if ($[2] !== closeCart || $[3] !== pathname) {
    t1 = () => {
      if (prevPathnameRef.current !== pathname) {
        prevPathnameRef.current = pathname;
        closeCart();
      }
    };
    t2 = [pathname, closeCart];
    $[2] = closeCart;
    $[3] = pathname;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] !== cart || $[7] !== isOpen) {
    t3 = () => {
      if (isOpen && cart && cart.total_quantity > 0 && !viewCartFiredRef.current) {
        trackViewCart(cart);
        viewCartFiredRef.current = true;
      }
      if (!isOpen) {
        viewCartFiredRef.current = false;
      }
    };
    t4 = [isOpen, cart];
    $[6] = cart;
    $[7] = isOpen;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== cart?.items) {
    t5 = cart?.items || [];
    $[10] = cart?.items;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const lineItems = t5;
  const isEmpty = lineItems.length === 0;
  let t6;
  if ($[12] !== closeCart) {
    t6 = open => {
      if (!open) {
        closeCart();
      }
    };
    $[12] = closeCart;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ShoppingBag className="w-6 h-6 text-gray-600" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("cart");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span>{t8}</span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== itemCount || $[20] !== t) {
    t10 = itemCount > 0 && <span className="text-gray-600">{t("itemCount", {
        count: itemCount
      })}</span>;
    $[19] = itemCount;
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <SheetTitle className="flex flex-row gap-2 items-center">{t7}{t9}{t10}</SheetTitle>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("closeCart");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <X className="w-6 h-6" />;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== closeCart || $[29] !== t12) {
    t14 = <Button variant="ghost" size="icon" onClick={closeCart} aria-label={t12}>{t13}</Button>;
    $[28] = closeCart;
    $[29] = t12;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t14) {
    t15 = <SheetHeader className="flex flex-row gap-2 items-center justify-between border-b">{t11}{t14}</SheetHeader>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== basePath || $[35] !== cart || $[36] !== closeCart || $[37] !== isEmpty || $[38] !== lineItems || $[39] !== loading || $[40] !== money || $[41] !== removeItem || $[42] !== t || $[43] !== tc || $[44] !== updateItem || $[45] !== updating) {
    t16 = <div className="flex-1 overflow-y-auto">{loading ? <div className="p-4 space-y-4">{[1, 2].map(_temp)}</div> : isEmpty ? <div className="flex flex-col items-center justify-center h-full p-8 text-center"><ShoppingBag className="w-16 h-16 text-gray-300 mb-4" strokeWidth={1} /><p className="text-gray-500 mb-4">{t("emptyCart")}</p><Link href={`${basePath}/products`} className="text-primary hover:text-primary font-medium" onClick={closeCart}>{tc("continueShopping")}</Link></div> : <ul className="divide-y divide-gray-200">{lineItems.map(item => <li key={item.id} className="p-4"><div className="flex gap-4"><Link href={`${basePath}/products/${item.slug}`} className="relative w-24 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0" onClick={closeCart}><ProductImage src={item.thumbnail_url} alt={item.name} fill={true} className="object-cover" sizes="96px" /></Link><div className="flex-1 min-w-0"><div className="flex justify-between items-start"><Link href={`${basePath}/products/${item.slug}`} className="font-medium text-gray-900 hover:text-primary line-clamp-2" onClick={closeCart}>{item.name}</Link><Button variant="destructive" size="icon-xs" onClick={async () => {
                  await removeItem(item.id);
                  if (cart) {
                    trackRemoveFromCart(item, cart.currency);
                  }
                }} disabled={updating} aria-label={t("removeItemLabel", {
                  name: item.name
                })}><Trash className="w-4 h-4" /></Button></div>{item.options_text && <p className="mt-1 text-sm text-gray-500">{item.options_text}</p>}<div className="mt-3 flex items-center justify-between"><QuantityPicker quantity={item.quantity} onDecrement={() => updateItem(item.id, Math.max(1, item.quantity - 1))} onIncrement={() => updateItem(item.id, item.quantity + 1)} disabled={updating} /><div className="text-sm font-medium">{item.compare_at_amount && item.price != null && parseFloat(item.compare_at_amount) > parseFloat(item.price) ? <><span className="text-gray-400 line-through mr-2">{money(item.compare_at_amount, item.currency)}</span><span className="text-red-600">{money(item.price, item.currency)}</span></> : <span className="text-gray-900">{money(item.price, item.currency)}</span>}</div></div></div></div></li>)}</ul>}</div>;
    $[34] = basePath;
    $[35] = cart;
    $[36] = closeCart;
    $[37] = isEmpty;
    $[38] = lineItems;
    $[39] = loading;
    $[40] = money;
    $[41] = removeItem;
    $[42] = t;
    $[43] = tc;
    $[44] = updateItem;
    $[45] = updating;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== basePath || $[48] !== cart || $[49] !== closeCart || $[50] !== isEmpty || $[51] !== loading || $[52] !== money || $[53] !== t || $[54] !== tc) {
    t17 = !isEmpty && !loading && <SheetFooter className="border-t border-gray-200 p-4 space-y-4"><div className="space-y-2"><div className="flex justify-between items-center"><span>{tc("subtotal")}</span><span>{money(cart?.item_total, cart?.currency)}</span></div>{cart && totalDiscount(cart) && <div className="flex justify-between items-center text-sm text-green-600"><span>{tc("discount")}</span><span>{money(totalDiscount(cart), cart.currency)}</span></div>}<div className="flex justify-between items-center"><span>{tc("shipping")}</span><span className="text-gray-500">{t("shippingCalculatedAtCheckout")}</span></div></div><div className="space-y-2"><Button size="lg" className="w-full" asChild={true}><Link href={`${basePath}/checkout/${cart?.id}`} onClick={closeCart}>{t("checkout")}</Link></Button><Button size="lg" className="w-full" variant="link" asChild={true}><Link href={`${basePath}/cart`} onClick={closeCart}>{t("viewCart")}</Link></Button></div></SheetFooter>;
    $[47] = basePath;
    $[48] = cart;
    $[49] = closeCart;
    $[50] = isEmpty;
    $[51] = loading;
    $[52] = money;
    $[53] = t;
    $[54] = tc;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== updating) {
    t18 = updating && <div className="absolute inset-0 bg-white/50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-gray-600 border-t-transparent rounded-full animate-spin" /></div>;
    $[56] = updating;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t18) {
    t19 = <SheetContent side="right" className="data-[side=right]:w-full data-[side=right]:sm:max-w-md flex flex-col p-0 gap-0" showCloseButton={false} aria-describedby={undefined}>{t15}{t16}{t17}{t18}</SheetContent>;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== isOpen || $[64] !== t19 || $[65] !== t6) {
    t20 = <Sheet open={isOpen} onOpenChange={t6}>{t19}</Sheet>;
    $[63] = isOpen;
    $[64] = t19;
    $[65] = t6;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  return t20;
}
function _temp(i) {
  return <div key={i} className="flex gap-4 animate-pulse"><div className="w-24 h-24 bg-gray-200 rounded" /><div className="flex-1 space-y-2"><div className="h-4 bg-gray-200 rounded w-3/4" /><div className="h-4 bg-gray-200 rounded w-1/2" /></div></div>;
}